import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import '../../styles/ManageBoatsPage.css';
import { showMessage } from '../../showMessage';

type BoatOption = { value: string; label: string };

type ApiBoat = {
  id: number;
  name: string;
  boat_id: number;
  boat_type: string;
  is_default?: boolean;
};

type BoatsResponse = {
  success: boolean;
  boats: ApiBoat[];
  defaultBoatId?: number | null;
  user?: {
    id: number;
    email: string;
    name?: string | null;
    lastName?: string | null;
    subscription_unlimited?: boolean;
  };
  message?: string;
};

type BoatMutationResponse = {
  success: boolean;
  boat: ApiBoat;
  defaultBoatId?: number | null;
  message?: string;
  polarsCleared?: boolean;
};

type BoatDraft = {
  name: string;
  boatType: string;
  isDefault: boolean;
};

type ClientBoat = {
  id: number;
  name: string;
  boatId: number;
  boatTypeLabel: string;
  isDefault: boolean;
};

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL ?? '');
const KEY_NOTIF_ERROR = 'notifications.error';
const KEY_BOATS_EMAIL_REQUIRED = 'admin.boats.emailRequired';
const KEY_BOATS_NAME_REQUIRED = 'admin.boats.boatNameRequired';
const KEY_BOATS_TYPE_REQUIRED = 'admin.boats.boatTypeRequired';
const KEY_BOATS_NETWORK = 'admin.boats.networkError';
const KEY_BOATS_LOAD_ERROR = 'admin.boats.loadError';
const KEY_BOATS_UPDATE_ERROR = 'admin.boats.updateError';
const KEY_BOATS_UPDATE_SUCCESS = 'admin.boats.updateSuccess';
const KEY_BOATS_CREATE_ERROR = 'admin.boats.createError';
const KEY_BOATS_CREATE_SUCCESS = 'admin.boats.createSuccess';
const KEY_BOATS_TYPE_LABEL = 'admin.boats.typeLabel';
const KEY_BOATS_NAME_LABEL = 'admin.boats.nameLabel';
const KEY_BOATS_SEARCH_LABEL = 'admin.boats.searchLabel';
const KEY_BOATS_SEARCH_PLACEHOLDER = 'admin.boats.searchPlaceholder';
const KEY_MISSING_AUTH = 'admin.missingAuthToken';
const LABEL_CLASS_NAME = 'manage-boats-label';
const KEY_DEFAULT_HINT = 'admin.boats.defaultHint';

type BoatRowProps = {
  boat: ClientBoat;
  draft: BoatDraft;
  boatOptions: BoatOption[];
  saving: boolean;
  canChangeBoatType: boolean;
  onSave: (boatId: number) => void;
  onChangeDraft: (boatId: number, partial: Partial<BoatDraft>) => void;
  onChangeDefault: (boatId: number, value: boolean) => void;
  t: ReturnType<typeof useTranslation>['t'];
};

const BoatRow = memo(function BoatRow({
  boat,
  draft,
  boatOptions,
  saving,
  canChangeBoatType,
  onSave,
  onChangeDraft,
  onChangeDefault,
  t,
}: BoatRowProps) {
  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      onChangeDraft(boat.id, { name: event.currentTarget.value }),
    [boat.id, onChangeDraft],
  );
  const handleTypeChange = useCallback(
    (value: string | null) => onChangeDraft(boat.id, { boatType: value ?? '' }),
    [boat.id, onChangeDraft],
  );
  const handleDefaultToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onChangeDefault(boat.id, event.currentTarget.checked),
    [boat.id, onChangeDefault],
  );
  const handleSaveClick = useCallback(() => onSave(boat.id), [boat.id, onSave]);

  return (
    <Card withBorder shadow="sm" className="boat-row-card" data-testid={`boat-row-${boat.id}`}>
      <div className="boat-row-header">
        <Group gap="xs">
          <Badge variant="outline" color="gray">
            #{boat.id}
          </Badge>
          {boat.isDefault && (
            <Badge color="green" variant="filled">
              {t('admin.boats.defaultLabel')}
            </Badge>
          )}
          <Text className="boat-type-chip">{boat.boatTypeLabel}</Text>
        </Group>
        <Button variant="light" loading={saving} onClick={handleSaveClick}>
          {t('admin.boats.saveBoat')}
        </Button>
      </div>

      <div className="boat-row-grid">
        <TextInput
          label={t(KEY_BOATS_NAME_LABEL)}
          value={draft.name}
          onChange={handleNameChange}
          classNames={{ label: LABEL_CLASS_NAME }}
        />
        <Select
          label={t(KEY_BOATS_TYPE_LABEL)}
          placeholder={t(KEY_BOATS_TYPE_LABEL)}
          data={boatOptions}
          value={draft.boatType}
          onChange={handleTypeChange}
          searchable
          clearable
          disabled={!canChangeBoatType}
          classNames={{ label: LABEL_CLASS_NAME }}
        />
        <Checkbox
          label={t(KEY_DEFAULT_HINT)}
          checked={draft.isDefault}
          onChange={handleDefaultToggle}
          className="boat-default-toggle"
        />
      </div>
    </Card>
  );
});

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export const ManageBoatsPage: FC = () => {
  const { t } = useTranslation('common');
  const [boatOptions, setBoatOptions] = useState<BoatOption[]>([]);
  const [emailQuery, setEmailQuery] = useState('');
  const [clientInfo, setClientInfo] = useState<{
    email: string;
    name?: string;
    lastName?: string;
    subscriptionUnlimited: boolean;
  } | null>(null);
  const [boats, setBoats] = useState<ClientBoat[]>([]);
  const [boatDrafts, setBoatDrafts] = useState<Record<number, BoatDraft>>({});
  const [loadingBoats, setLoadingBoats] = useState(false);
  const [savingBoats, setSavingBoats] = useState<Record<number, boolean>>({});
  const [creatingBoat, setCreatingBoat] = useState(false);

  const createBoatForm = useForm({
    initialValues: {
      email: '',
      boatName: '',
      boatType: '',
      isDefault: false,
    },
    validate: {
      email: (value) => (value.trim() ? null : t(KEY_BOATS_EMAIL_REQUIRED)),
      boatName: (value) => (value.trim() ? null : t(KEY_BOATS_NAME_REQUIRED)),
      boatType: (value) => (value ? null : t(KEY_BOATS_TYPE_REQUIRED)),
    },
  });

  const baseDraftForBoat = useCallback(
    (boat: ClientBoat): BoatDraft => ({
      name: boat.name,
      boatType: String(boat.boatId),
      isDefault: boat.isDefault,
    }),
    [],
  );

  const clientFullName = useMemo(() => {
    if (!clientInfo) return '';
    const parts = [clientInfo.name ?? '', clientInfo.lastName ?? '']
      .map((part) => part?.trim())
      .filter(Boolean);
    return parts.join(' ');
  }, [clientInfo]);

  useEffect(() => {
    const fetchBoatOptions = async (): Promise<void> => {
      const tokenRaw = localStorage.getItem('token');
      if (!tokenRaw) {
        showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_MISSING_AUTH));
        return;
      }
      try {
        const response = await axios.post<{
          success: boolean;
          boats: Array<{ id: number; name: string; loa: number }>;
          message?: string;
        }>(
          `${API_BASE_URL}/api/user/getAllBoats`,
          {},
          { headers: { Authorization: `Bearer ${tokenRaw}` } },
        );

        if (response.data.success) {
          const options: BoatOption[] = response.data.boats.map((boat) => ({
            value: String(boat.id),
            label: `${boat.name} (${boat.loa}ft)`,
          }));
          setBoatOptions(options);
        } else {
          showMessage('error', t(KEY_NOTIF_ERROR), response.data.message ?? t('admin.failedLoadBoats'));
        }
      } catch {
        showMessage('error', t(KEY_NOTIF_ERROR), t('admin.networkLoadBoats'));
      }
    };

    void fetchBoatOptions();
  }, [t]);

  const syncDraftsFromBoats = useCallback(
    (nextBoats: ClientBoat[]) => {
      setBoatDrafts(() => {
        const drafts: Record<number, BoatDraft> = {};
        for (const boat of nextBoats) {
          drafts[boat.id] = baseDraftForBoat(boat);
        }
        return drafts;
      });
    },
    [baseDraftForBoat],
  );

  const handleSearch = async (): Promise<void> => {
    const normalizedEmail = normalizeEmail(emailQuery);
    if (!normalizedEmail) {
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_BOATS_EMAIL_REQUIRED));
      return;
    }
    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_MISSING_AUTH));
      return;
    }
    setLoadingBoats(true);
    try {
      const response = await axios.post<BoatsResponse>(
        `${API_BASE_URL}/api/user/admin/getClientBoats`,
        { email: normalizedEmail },
        { headers: { Authorization: `Bearer ${tokenRaw}` } },
      );

      if (!response.data.success) {
        showMessage('error', t(KEY_NOTIF_ERROR), response.data.message ?? t(KEY_BOATS_LOAD_ERROR));
        return;
      }

      const defaultBoatId = response.data.defaultBoatId ?? null;
      const mappedBoats: ClientBoat[] = (response.data.boats ?? []).map((boat) => ({
        id: boat.id,
        name: boat.name,
        boatId: boat.boat_id,
        boatTypeLabel: boat.boat_type,
        isDefault: defaultBoatId === boat.id || boat.is_default === true,
      }));

      setClientInfo({
        email: response.data.user?.email ?? normalizedEmail,
        name: response.data.user?.name ?? undefined,
        lastName: response.data.user?.lastName ?? undefined,
        subscriptionUnlimited: response.data.user?.subscription_unlimited === true,
      });
      setBoats(mappedBoats);
      syncDraftsFromBoats(mappedBoats);
    } catch (error) {
      console.error('Error loading boats:', error);
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_BOATS_NETWORK));
    } finally {
      setLoadingBoats(false);
    }
  };

  const handleDraftChange = useCallback(
    (boatId: number, partial: Partial<BoatDraft>) => {
      setBoatDrafts((prev) => {
        const foundBoat = boats.find((boat) => boat.id === boatId);
        const base =
          prev[boatId] ??
          (foundBoat ? baseDraftForBoat(foundBoat) : { name: '', boatType: '', isDefault: false });
        return {
          ...prev,
          [boatId]: { ...base, ...partial },
        };
      });
    },
    [baseDraftForBoat, boats],
  );

  const handleDefaultChange = useCallback(
    (boatId: number, value: boolean) => {
      setBoatDrafts((prev) => {
        const next: Record<number, BoatDraft> = { ...prev };
        for (const boat of boats) {
          const current = next[boat.id] ?? baseDraftForBoat(boat);
          next[boat.id] = {
            ...current,
            isDefault: boat.id === boatId ? value : value ? false : current.isDefault,
          };
        }
        return next;
      });
    },
    [baseDraftForBoat, boats],
  );

  const handleSaveBoat = useCallback(async (boatId: number): Promise<void> => {
    const boat = boats.find((b) => b.id === boatId);
    if (!boat) {
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_BOATS_LOAD_ERROR));
      return;
    }
    const draft = boatDrafts[boatId] ?? baseDraftForBoat(boat);
    if (!draft.boatType) {
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_BOATS_TYPE_REQUIRED));
      return;
    }
    if (!clientInfo?.email) {
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_BOATS_EMAIL_REQUIRED));
      return;
    }
    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_MISSING_AUTH));
      return;
    }

    setSavingBoats((prev) => ({ ...prev, [boatId]: true }));
    try {
      const response = await axios.post<BoatMutationResponse>(
        `${API_BASE_URL}/api/user/admin/updateClientBoat`,
        {
          email: clientInfo.email,
          boatId,
          boatName: draft.name.trim(),
          boatType: Number.parseInt(draft.boatType, 10),
          isDefault: draft.isDefault,
        },
        { headers: { Authorization: `Bearer ${tokenRaw}` } },
      );

      if (!response.data.success) {
        showMessage('error', t(KEY_NOTIF_ERROR), response.data.message ?? t(KEY_BOATS_UPDATE_ERROR));
        return;
      }

      const defaultBoatId = response.data.defaultBoatId ?? null;
      const updatedBoat: ClientBoat = {
        id: response.data.boat.id,
        name: response.data.boat.name,
        boatId: response.data.boat.boat_id,
        boatTypeLabel: response.data.boat.boat_type,
        isDefault: defaultBoatId === response.data.boat.id,
      };

      let updatedSnapshot: ClientBoat[] = [];
      setBoats((prev) => {
        const next = prev.map((b) =>
          b.id === boatId ? updatedBoat : { ...b, isDefault: defaultBoatId === b.id },
        );
        updatedSnapshot = next;
        return next;
      });
      syncDraftsFromBoats(updatedSnapshot);

      showMessage(
        'success',
        t('notifications.success'),
        response.data.polarsCleared
          ? t('admin.boats.modelChangedPolarsCleared')
          : response.data.message ?? t(KEY_BOATS_UPDATE_SUCCESS),
      );
    } catch (error) {
      console.error('Error updating boat:', error);
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_BOATS_NETWORK));
    } finally {
      setSavingBoats((prev) => ({ ...prev, [boatId]: false }));
    }
  }, [
    baseDraftForBoat,
    boatDrafts,
    boats,
    clientInfo,
    syncDraftsFromBoats,
    t,
  ]);

  const handleCreateBoat = async (values: {
    email: string;
    boatName: string;
    boatType: string;
    isDefault: boolean;
  }): Promise<void> => {
    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_MISSING_AUTH));
      return;
    }

    setCreatingBoat(true);
    const normalizedEmail = normalizeEmail(values.email);
    try {
      const response = await axios.post<BoatMutationResponse>(
        `${API_BASE_URL}/api/user/admin/createClientBoat`,
        {
          email: normalizedEmail,
          boatName: values.boatName.trim(),
          boatType: Number.parseInt(values.boatType, 10),
          isDefault: values.isDefault,
        },
        { headers: { Authorization: `Bearer ${tokenRaw}` } },
      );

      if (!response.data.success) {
        showMessage('error', t(KEY_NOTIF_ERROR), response.data.message ?? t(KEY_BOATS_CREATE_ERROR));
        return;
      }

      const defaultBoatId = response.data.defaultBoatId ?? null;
      const newBoat: ClientBoat = {
        id: response.data.boat.id,
        name: response.data.boat.name,
        boatId: response.data.boat.boat_id,
        boatTypeLabel: response.data.boat.boat_type,
        isDefault: defaultBoatId === response.data.boat.id,
      };

      // If we are looking at this client, append the new boat locally
      if (clientInfo && normalizeEmail(clientInfo.email) === normalizedEmail) {
        let updatedSnapshot: ClientBoat[] = [];
        setBoats((prev) => {
          const next = [
            ...prev.map((boat) => ({ ...boat, isDefault: defaultBoatId === boat.id })),
            newBoat,
          ];
          updatedSnapshot = next;
          return next;
        });
        syncDraftsFromBoats(updatedSnapshot);
      }

      showMessage('success', t('notifications.success'), response.data.message ?? t(KEY_BOATS_CREATE_SUCCESS));
      createBoatForm.reset();
      createBoatForm.setFieldValue('email', normalizedEmail);
    } catch (error) {
      console.error('Error creating boat:', error);
      showMessage('error', t(KEY_NOTIF_ERROR), t(KEY_BOATS_NETWORK));
    } finally {
      setCreatingBoat(false);
    }
  };

  return (
    <div className="manage-boats-page">
      <div className="manage-boats-header">
        <Title order={2} className="manage-boats-title">
          {t('admin.manageBoats')}
        </Title>
        <Text className="manage-boats-subtitle">{t('admin.boats.subtitle')}</Text>
      </div>

      <Card shadow="md" radius="md" className="manage-boats-card" withBorder>
        <LoadingOverlay visible={loadingBoats} zIndex={10} />
        <div className="manage-boats-search">
          <TextInput
            label={t(KEY_BOATS_SEARCH_LABEL)}
            placeholder={t(KEY_BOATS_SEARCH_PLACEHOLDER)}
            value={emailQuery}
            onChange={(event) => setEmailQuery(event.currentTarget.value)}
            classNames={{ label: LABEL_CLASS_NAME }}
          />
          <Button onClick={handleSearch} loading={loadingBoats} className="manage-boats-search-button">
            {t('admin.boats.searchButton')}
          </Button>
        </div>

        {clientInfo && (
          <div className="manage-boats-client">
            <Group gap="xs">
              <Text className="manage-boats-client-email">
                {t('admin.boats.listTitle', { email: clientInfo.email })}
              </Text>
              {clientFullName && (
                <Badge variant="light" color="blue">
                  {t('admin.boats.clientName', { name: clientFullName })}
                </Badge>
              )}
            </Group>
          </div>
        )}

        <Divider className="manage-boats-divider" />

        <Stack gap="md" className="manage-boats-list">
          {clientInfo && boats.length === 0 && (
            <Text className="manage-boats-empty">{t('admin.boats.noBoats')}</Text>
          )}

          {!clientInfo && (
            <Text className="manage-boats-empty">{t('admin.boats.clientNotLoaded')}</Text>
          )}

          {boats.map((boat) => {
            const draft = boatDrafts[boat.id] ?? baseDraftForBoat(boat);
            return (
              <BoatRow
                key={boat.id}
                boat={boat}
                draft={draft}
                boatOptions={boatOptions}
                saving={savingBoats[boat.id] ?? false}
                canChangeBoatType={clientInfo?.subscriptionUnlimited === true}
                onSave={handleSaveBoat}
                onChangeDraft={handleDraftChange}
                onChangeDefault={handleDefaultChange}
                t={t}
              />
            );
          })}
        </Stack>
      </Card>

      <Card shadow="md" radius="md" className="manage-boats-card" withBorder>
        <Title order={4} className="manage-boats-section-title">
          {t('admin.boats.createTitle')}
        </Title>
        <form onSubmit={createBoatForm.onSubmit(handleCreateBoat)}>
          <div className="create-boat-grid">
            <TextInput
              label={t(KEY_BOATS_SEARCH_LABEL)}
              placeholder={t(KEY_BOATS_SEARCH_PLACEHOLDER)}
              classNames={{ label: LABEL_CLASS_NAME }}
              {...createBoatForm.getInputProps('email')}
            />
            <TextInput
              label={t(KEY_BOATS_NAME_LABEL)}
              placeholder={t(KEY_BOATS_NAME_LABEL)}
              classNames={{ label: LABEL_CLASS_NAME }}
              {...createBoatForm.getInputProps('boatName')}
            />
            <Select
              label={t(KEY_BOATS_TYPE_LABEL)}
              placeholder={t(KEY_BOATS_TYPE_LABEL)}
              data={boatOptions}
              searchable
              clearable
              classNames={{ label: LABEL_CLASS_NAME }}
              {...createBoatForm.getInputProps('boatType')}
            />
            <Checkbox
              label={t('admin.boats.defaultHint')}
              className="boat-default-toggle"
              {...createBoatForm.getInputProps('isDefault', { type: 'checkbox' })}
            />
            <Button type="submit" loading={creatingBoat} className="create-boat-button">
              {t('admin.boats.createButton')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
